import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { notification } from "antd";
import { requiredRule } from "./useBasicComponent";
import { useResourceContext } from "@/layouts/ResourceContext";
import { useTemplateComponent } from "./useTemplateComponent";
import { machineTypeEnum } from "@/hooks/config/useNodegroupConfig";
import { fetchAZ } from "@/hooks/config/useGeneralConfig";

export const useNodegroupGeneralConfig = ({
  formRef,
  onValuesChangeCallback,
}) => {
  const { t } = useTranslation();

  const [contextLoading, setContextLoading] = useState(true);
  const [EKSNameEnum, setEKSNameEnum] = useState({});
  const [azEnum, setAZEnum] = useState({});
  const [zoneLoading, setZoneLoading] = useState(true);
  const [regionEnum, setRegionEnum] = useState({});
  const { templateComponent } = useTemplateComponent({
    formRef,
    onValuesChangeCallback,
  });

  const {
    rootState: { eks: context },
  } = useResourceContext();

  useEffect(() => {
    const fetchContext = async () => {
      if (context.isError) {
        setContextLoading(true);
        notification.error({
          description: t(
            "config.instance.error.context",
            "Fail to get context from server.",
          ),
        });
        return;
      }
      if (context.isLoading) {
        setContextLoading(true);
        notification.error({
          description: t(
            "config.nodegroup.error.contextLoading",
            "Loading context from server. Please re-launch nodegroup later.",
          ),
        });
        return;
      }

      const { eks, region: eksRegion } = context.data;
      setEKSNameEnum({ [eks]: { text: eks } });
      setRegionEnum({ [eksRegion]: { text: eksRegion } });
      formRef.current.setFieldsValue({
        eks,
        region: eksRegion,
      });

      setContextLoading(false);
      await fetchAZ(eksRegion, setAZEnum, setZoneLoading, t);
    };

    fetchContext();
  }, [JSON.stringify(context)]);

  const rawSchema = [
    {
      i18nTitle: "config.instance.form.id",
      title: "ID",
      dataIndex: "id",
      formItemProps: {
        rules: [...requiredRule(t)],
      },
      width: "400px",
    },
    {
      i18nTitle: "config.instance.form.type",
      title: "Type",
      dataIndex: "nodegroupType",
      valueType: "select",
      valueEnum: { shared: { text: "Shared (for pool)" } },
      initialValue: "shared",
      width: "400px",
      fieldProps: {
        disabled: true,
      },
    },
    {
      i18nTitle: "config.instance.form.eksName",
      title: "EKS Name",
      dataIndex: "eks",
      valueType: "select",
      valueEnum: EKSNameEnum,
      width: "400px",
      fieldProps: {
        disabled: true,
        loading: contextLoading,
      },
    },
    templateComponent(),

    {
      i18nTitle: "config.instance.form.region",
      title: "Region",
      dataIndex: "region",
      valueType: "select",
      valueEnum: regionEnum,
      fieldProps: {
        disabled: true,
        loading: contextLoading,
      },
      width: "400px",
    },
    {
      i18nTitle: "config.instance.form.zone",
      title: "AZ",
      dataIndex: "zone",
      valueType: "select",
      valueEnum: azEnum,
      formItemProps: {
        rules: [...requiredRule(t)],
      },
      fieldProps: {
        mode: 'multiple',
        loading: zoneLoading,
      },
      width: "400px",
    },
    // {
    //   i18nTitle: "config.instance.form.machineType",
    //   title: "Machine Type",
    //   dataIndex: "instanceTypes",
    //   valueType: "select",
    //   valueEnum: machineTypeEnum,
    //   formItemProps: {
    //     rules: [...requiredRule(t)],
    //   },
    //   width: "md",
    // },
    {
      i18nTitle: "config.instance.form.autoScaled",
      title: "Auto Scaled",
      dataIndex: "autoscaled",
      valueType: "switch",
      initialValue: false,
    },
  ];

  return rawSchema;
};
